import React, { useState, useMemo } from "react";
import { ClientWorkOrderItem } from "../types/clients-module-types";
import { dateFormatEntryEdit } from "../../SharedModule/utils/formatters";
import {
  changeWorkOrdersOrder,
  SelectEndDateWorkOrder,
  SelectSearchTextWorkOrder,
  SelectStartDateWorkOrder,
  SelectWorkOrdersOrderCriteria,
  setEndDateWorkOrder,
  setSearchTextWorkOrder,
  setStartDateWorkOrder,
} from "../redux/reducers/clientWork.reducer";
import { useDispatch, useSelector } from "react-redux";
import { transformValue } from "../../TimesModule/utils/timesUtils";
import { Calendar } from "../../SharedModule/components/Calendar";
import { BiError } from "react-icons/bi";
import { NoContent } from "../../SharedModule/components/NoContent";
import image from "./../../styles/legacy/404.png";

export const AcordionWorkOrdersTable = React.memo(
  ({
    clientWorkOrders,
  }: {
    clientWorkOrders: Array<ClientWorkOrderItem> | null;
  }) => {
    const dispatch = useDispatch();

    const sortBy = useSelector(SelectWorkOrdersOrderCriteria);
    const selectSearchTextWorkOrder = useSelector(SelectSearchTextWorkOrder);
    const startDateWorkOrder = useSelector(SelectStartDateWorkOrder);
    const endDateWorkOrder = useSelector(SelectEndDateWorkOrder);

    const [projectsSearchText, setProjectsSearchText] = useState(
      selectSearchTextWorkOrder
    );
    const [dateFrom, setDateFrom] = useState<any>(startDateWorkOrder);
    const [dateTo, setDateTo] = useState<any>(endDateWorkOrder);

    const updateDateFilter = (type: string, date: string) => {
      if (type === "from") {
        dispatch(setStartDateWorkOrder(new Date(date)));
        setDateFrom(new Date(date));
      } else {
        dispatch(setEndDateWorkOrder(new Date(date)));
        setDateTo(new Date(date));
      }
    };

    let clientWorkOrdersToShow: Array<ClientWorkOrderItem> = useMemo(
      () =>
        (clientWorkOrders ?? []).filter((elem) => {
          const lowerSearch = projectsSearchText.toLocaleLowerCase();
          return (
            elem.id.toString().toLocaleLowerCase().includes(lowerSearch) ||
            elem.description?.toLocaleLowerCase().includes(lowerSearch)
          );
        }),
      [clientWorkOrders, projectsSearchText]
    );

    const search = (value: string) => {
      setProjectsSearchText(value);
    };

    const blurHandler = () => {
      dispatch(setSearchTextWorkOrder(projectsSearchText));
    };

    const goToWorkOrderPage = (id: number) => {
      const baseUrl = process.env.REACT_APP_ASUITE_WO_URI ?? "";
      const newWindow = window.open(baseUrl + id, "_blank");
      if (newWindow) {
        newWindow.focus();
      }
    };

    return (
      <>
        {!clientWorkOrders ||
          (clientWorkOrders.length === 0 && (
            <NoContent
              title="No Work Orders for this Client"
              image={image}
              style={{ margin: "0 auto" }}
            />
          ))}

        {clientWorkOrders && clientWorkOrders.length > 0 && (
          <>
            <div className="d-flex mb-3 mx-2 justify-content-between">
              <div className="d-flex flex-column col-sm-7">
                <label htmlFor="wo-search" className="col-form-label">
                  Search
                </label>
                <input
                  id="wo-search"
                  type="text"
                  onBlur={blurHandler}
                  className="form-control form-control-sm common-input"
                  placeholder="Search by ID or description"
                  value={projectsSearchText}
                  onChange={(event) => search(event.target.value)}
                />
              </div>

              <div className="d-flex flex-column col-sm-4 justify-content-end">
                <div className="d-flex">
                  <div className="position-relative">
                    <label htmlFor="wo-date-from" className="form-label">
                      From
                    </label>
                    <Calendar
                      id="wo-date-from"
                      date={dateFrom}
                      onChange={(date: string) =>
                        updateDateFilter("from", date)
                      }
                    />
                  </div>
                  <div className="position-relative ms-5">
                    <label htmlFor="wo-date-to" className="form-label">
                      To
                    </label>
                    <Calendar
                      id="wo-date-to"
                      date={dateTo}
                      onChange={(date: string) => updateDateFilter("to", date)}
                      className={`${dateFrom > dateTo ? "border-error" : ""}`}
                      minDate={dateFrom}
                    />
                  </div>
                </div>
                {dateFrom > dateTo && (
                  <div className="error-message my-2">
                    <BiError className="error-icon" />
                    <span className="ms-1">
                      The To date cannot be lower than the From date.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {clientWorkOrdersToShow?.filter(
              (elem) =>
                new Date(elem.openedDate) > dateFrom &&
                new Date(elem.closedDate) < dateTo
            ).length === 0 && (
              <NoContent
                title="No Results"
                text="Edit your filters to find the work items you are looking for."
                image={image}
                style={{ margin: "0 auto" }}
              />
            )}

            {clientWorkOrdersToShow?.filter(
              (elem) =>
                new Date(elem.openedDate) > dateFrom &&
                new Date(elem.closedDate) < dateTo
            ).length > 0 && (
              <table
                className="table grey-table-header-background mt-4 mb-2"
                style={{ width: "100%" }}
              >
                <thead className="align-middle sticky sticky-header">
                  <tr>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-10 pointer ${
                        sortBy.orderBy === "id"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="id"
                      key="id"
                      onClick={() =>
                        dispatch(
                          changeWorkOrdersOrder({
                            orderBy: "id",
                            dataType: "number",
                          })
                        )
                      }
                    >
                      ID
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-15 pointer ${
                        sortBy.orderBy === "openedDate"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="openedDate"
                      key="openedDate"
                      onClick={() =>
                        dispatch(
                          changeWorkOrdersOrder({
                            orderBy: "openedDate",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Opened Date
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-15 pointer ${
                        sortBy.orderBy === "closedDate"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="closedDate"
                      key="closedDate"
                      onClick={() =>
                        dispatch(
                          changeWorkOrdersOrder({
                            orderBy: "closedDate",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Closed Date
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-20 pointer ${
                        sortBy.orderBy === "description"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="description"
                      key="description"
                      onClick={() =>
                        dispatch(
                          changeWorkOrdersOrder({
                            orderBy: "description",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Description
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-15 pointer ${
                        sortBy.orderBy === "ownerName"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="ownerName"
                      key="ownerName"
                      onClick={() =>
                        dispatch(
                          changeWorkOrdersOrder({
                            orderBy: "ownerName",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Owner
                    </th>
                    <th
                      className={`border-dark border-top fw-600 ps-4 column-25 pointer ${
                        sortBy.orderBy === "usersAssignees"
                          ? "sorted-" + sortBy.criteria
                          : ""
                      }`}
                      id="usersAssignees"
                      key="usersAssignees"
                      onClick={() =>
                        dispatch(
                          changeWorkOrdersOrder({
                            orderBy: "usersAssignees",
                            dataType: "string",
                          })
                        )
                      }
                    >
                      Asignees
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clientWorkOrdersToShow
                    ?.filter(
                      (elem) =>
                        new Date(elem.openedDate) > dateFrom &&
                        new Date(elem.closedDate) < dateTo
                    )
                    .map((item: ClientWorkOrderItem) => {
                      return (
                        <tr
                          key={item.id}
                          className="client-contant-row-no-clickable"
                        >
                          <td className="ps-4 fw-500">
                            <span
                              className="link-text link-text-underline"
                              role="link"
                              tabIndex={0}
                              onClick={() => goToWorkOrderPage(item.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  goToWorkOrderPage(item.id);
                                }
                              }}
                            >
                              {item.id}
                            </span>
                          </td>
                          <td className="ps-4 fw-500">
                            {dateFormatEntryEdit(item.openedDate)}
                          </td>
                          <td className="ps-4 fw-500">
                            {item.closedDate
                              ? dateFormatEntryEdit(item.closedDate)
                              : ""}
                          </td>
                          <td className="ps-4 fw-500">{item.description}</td>
                          <td className="ps-4 fw-500">
                            {item.ownerName
                              ? `${item.ownerName} ${
                                  item.ownerActive ? "" : "(X)"
                                }`
                              : ""}
                          </td>
                          <td className="ps-4 fw-500">
                            {item.usersAssignees
                              ? transformValue(
                                  "usersAssignees",
                                  item.usersAssignees
                                )
                              : ""}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </>
        )}
      </>
    );
  }
);
